import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, RefreshCw, Truck } from 'lucide-react';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useAppSelector } from '@/Redux/store';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Table, TableWrap, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import PanelResult from './PanelResult';
import CreateTransferDialog from './CreateTransferDialog';
import TransferDetailDialog from './TransferDetailDialog';
import {
  CK_ROUTES,
  CK_PERMISSIONS,
  StockTransferStatus,
  type StockTransfer,
  type InTransitRow,
} from '../centralKitchenApi';
import { transferStatusTone, formatDateTime, formatQuantity } from '../lib';

const STATUSES: readonly StockTransferStatus[] = Object.values(StockTransferStatus);

/**
 * Warehouse-to-branch transfers, and — at the top — the in-transit board: the
 * screen this epic exists for (ADR-0015 §3). Stock that has left the
 * commissary and not yet arrived at the branch is counted at neither end and
 * belongs to nobody; the list here decomposes exactly what is on which van,
 * which is the honest answer to "where did that stock go" while it is still
 * on the road.
 */
export default function TransfersPanel() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const activeBranchId = useAppSelector((state) => state.ui.activeBranchId);

  const [status, setStatus] = useState<StockTransferStatus | ''>('');
  const [createOpen, setCreateOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const inTransit = useApi<unknown>(
    can(CK_PERMISSIONS.transfersView) ? CK_ROUTES.transferInTransit : null,
  );
  const { rows: inTransitRows } = rowsOf<InTransitRow>(inTransit.data);

  const listParams = useMemo(() => ({ ...(status ? { status } : {}), limit: 100 }), [status]);
  const transfers = useApi<unknown>(
    can(CK_PERMISSIONS.transfersView) ? CK_ROUTES.transfers : null,
    listParams,
  );
  const { rows } = rowsOf<StockTransfer>(transfers.data);

  const reload = () => {
    inTransit.reload();
    transfers.reload();
  };

  return (
    <div className="space-y-5">
      {/* ---- the in-transit board ---- */}
      <section className="space-y-2">
        <p className="flex items-center gap-2 text-sm font-medium">
          <Icon as={Truck} size="sm" className="text-sky-600 dark:text-sky-400" />
          {t('centralKitchen.transfers.inTransitTitle')}
        </p>
        <TableWrap>
          {inTransit.loading ? null : inTransit.error ? (
            <div role="alert" className="p-4 text-sm text-red-700 dark:text-red-300">
              {t('centralKitchen.states.loadFailed', { subject: t('centralKitchen.transfers.inTransitSubject') })}: {inTransit.error}
            </div>
          ) : inTransitRows.length === 0 ? (
            <p className="p-4 text-sm text-ink-muted">{t('centralKitchen.transfers.inTransitEmpty')}</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>{t('centralKitchen.transfers.columns.transfer')}</TH>
                  <TH>{t('centralKitchen.transfers.inTransitBoundFor')}</TH>
                  <TH>{t('centralKitchen.transfers.columns.item')}</TH>
                  <TH className="text-end">{t('centralKitchen.transfers.columns.quantity')}</TH>
                  <TH>{t('centralKitchen.transfers.columns.dispatched')}</TH>
                </TR>
              </THead>
              <tbody>
                {inTransitRows.map((row, i) => (
                  <TR key={`${row.transferId}-${row.inventoryItemId}-${i}`} className="cursor-pointer" onClick={() => setOpenId(row.transferId)}>
                    <TD className="font-mono text-xs">{row.transferNumber}</TD>
                    <TD>{row.toWarehouse?.name ?? row.toWarehouseId}</TD>
                    <TD>{row.inventoryItem?.name ?? row.inventoryItemId}</TD>
                    <TD className="text-end tabular-nums">{formatQuantity(row.quantity, i18n.language)}</TD>
                    <TD className="text-xs text-ink-muted">{formatDateTime(row.dispatchedAt, i18n.language)}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </TableWrap>
      </section>

      {/* ---- the transfer list ---- */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <Field label={t('centralKitchen.transfers.filterStatus')} controlId="ck-transfer-status">
            <Select value={status} onChange={(e) => setStatus(e.target.value as StockTransferStatus | '')}>
              <option value="">{t('centralKitchen.transfers.allStatuses')}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`centralKitchen.transferStatus.${s}`)}
                </option>
              ))}
            </Select>
          </Field>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={reload}>
              <Icon as={RefreshCw} />
              {t('common.refresh')}
            </Button>
            {can(CK_PERMISSIONS.transfersCreate) && (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Icon as={Plus} />
                {t('centralKitchen.transfers.new')}
              </Button>
            )}
          </div>
        </div>

        <TableWrap>
          <PanelResult
            loading={transfers.loading}
            error={transfers.error}
            count={rows.length}
            onRetry={transfers.reload}
            subject={t('centralKitchen.transfers.subject')}
            filtered={!!status}
            onClearFilters={() => setStatus('')}
            emptyTitle={t('centralKitchen.transfers.emptyTitle')}
            emptyHint={t('centralKitchen.transfers.emptyHint')}
          >
            <Table>
              <THead>
                <TR>
                  <TH>{t('centralKitchen.transfers.columns.transfer')}</TH>
                  <TH>{t('centralKitchen.transfers.columns.status')}</TH>
                  <TH>{t('centralKitchen.transfers.columns.from')}</TH>
                  <TH>{t('centralKitchen.transfers.columns.to')}</TH>
                  <TH className="text-end">{t('centralKitchen.transfers.columns.lines')}</TH>
                  <TH>{t('centralKitchen.transfers.columns.dispatched')}</TH>
                  <TH>{t('centralKitchen.transfers.columns.received')}</TH>
                  <TH />
                </TR>
              </THead>
              <tbody>
                {rows.length === 0 ? (
                  <TableEmpty colSpan={8}>{t('centralKitchen.transfers.emptyTitle')}</TableEmpty>
                ) : (
                  rows.map((tr) => (
                    <TR key={tr.id} className="cursor-pointer" onClick={() => setOpenId(tr.id)}>
                      <TD className="font-mono text-xs">{tr.number}</TD>
                      <TD>
                        <Badge tone={transferStatusTone(tr.status)}>{t(`centralKitchen.transferStatus.${tr.status}`)}</Badge>
                      </TD>
                      <TD>{tr.fromWarehouse?.name ?? tr.fromWarehouseId}</TD>
                      <TD>{tr.toWarehouse?.name ?? tr.toWarehouseId}</TD>
                      <TD className="text-end tabular-nums">{tr._count?.lines ?? tr.lines?.length ?? '—'}</TD>
                      <TD className="text-xs text-ink-muted">{formatDateTime(tr.dispatchedAt, i18n.language)}</TD>
                      <TD className="text-xs text-ink-muted">{formatDateTime(tr.receivedAt, i18n.language)}</TD>
                      <TD>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setOpenId(tr.id); }}>
                          {t('common.view')}
                        </Button>
                      </TD>
                    </TR>
                  ))
                )}
              </tbody>
            </Table>
          </PanelResult>
        </TableWrap>
      </section>

      <CreateTransferDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          setCreateOpen(false);
          reload();
          setOpenId(id);
        }}
        defaultBranchId={activeBranchId}
      />
      <TransferDetailDialog transferId={openId} onClose={() => setOpenId(null)} onChanged={reload} />
    </div>
  );
}
